import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { UserPlus, ChevronRight } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import {
  Candidate, CandidateStage, HireArgs, useCandidate, useCandidates, useCreateCandidate, useHireCandidate,
  useStageSummary, useTransitionCandidate,
} from '@/lib/hiring';
import { confirm, promptText } from '@/components/ui/ConfirmDialog';
import { CurrencyPicker } from '@/components/ui/CurrencyPicker';
import { CountryPicker } from '@/components/ui/CountryPicker';
import { Combobox, type ComboboxItem } from '@/components/ui/Combobox';

const STAGES: { key: CandidateStage; label: string }[] = [
  { key: 'applied',    label: 'Applied' },
  { key: 'screen',     label: 'Screen' },
  { key: 'interview',  label: 'Interview' },
  { key: 'offer',      label: 'Offer' },
  { key: 'hired',      label: 'Hired' },
];

const STAGE_ACCENT: Record<CandidateStage, string> = {
  applied:   'from-sky-500 to-indigo-500',
  screen:    'from-indigo-500 to-violet-500',
  interview: 'from-violet-500 to-fuchsia-500',
  offer:     'from-fuchsia-500 to-rose-500',
  hired:     'from-emerald-500 to-teal-500',
  rejected:  'from-rose-500 to-rose-600',
  withdrawn: 'from-slate-400 to-slate-500',
};

export function HiringPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const summary = useStageSummary();
  const candidates = useCandidates();
  const create = useCreateCandidate();
  const transition = useTransitionCandidate();
  const hire = useHireCandidate();
  const [showAdd, setShowAdd] = useState(false);
  const [hiring, setHiring] = useState<Candidate | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('hiring.eyebrow')}</p>
            <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('hiring.title')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('hiring.subtitle')}</p>
          </div>
          <button
            onClick={() => setShowAdd(true)}
            className="gradient-primary inline-flex h-11 items-center gap-2 px-5 rounded-xl text-white text-sm font-semibold shadow-glow hover:shadow-glow-pink transition"
          >
            <UserPlus className="h-4 w-4" />
            {t('hiring.addCandidate')}
          </button>
        </header>

        {/* Stage pipeline */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {STAGES.map((s) => {
            const count = summary.data?.[s.key] ?? 0;
            return (
              <motion.div
                key={s.key}
                whileHover={{ y: -3 }}
                className="relative overflow-hidden rounded-2xl border border-border bg-card p-4 shadow-soft"
              >
                <div className={`pointer-events-none absolute -top-10 -end-10 h-24 w-24 rounded-full bg-gradient-to-br ${STAGE_ACCENT[s.key]} opacity-15 blur-2xl`} />
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{t(`hiring.stage.${s.key}`)}</p>
                <p className="num mt-1 text-2xl font-bold">{count}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Candidate list */}
        <div className="rounded-2xl border border-border bg-card shadow-soft overflow-hidden">
          <div className="px-5 py-3 border-b border-border bg-muted/30">
            <h2 className="text-sm font-semibold">{t('hiring.candidates')}</h2>
          </div>
          {candidates.isLoading ? (
            <p className="px-5 py-8 text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : candidates.isError ? (
            <div className="px-5 py-12 text-center">
              <p className="text-sm font-medium text-rose-600">
                {t('hiring.loadError', { defaultValue: "Couldn't load candidates. Try again." })}
              </p>
              <button
                onClick={() => candidates.refetch()}
                className="mt-3 inline-flex h-9 items-center px-4 rounded-lg bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20"
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          ) : (candidates.data ?? []).length === 0 ? (
            <p className="px-5 py-12 text-center text-sm text-muted-foreground">
              {t('hiring.empty')}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {(candidates.data ?? []).map((c) => (
                <CandidateRow
                  key={c.id}
                  c={c}
                  onSelect={() => setSelectedId(c.id)}
                  onAdvance={(stage) => {
                    // Advancing past 'offer' triggers the proper Hire dialog so
                    // a real Employee row is created — not just a stage change.
                    if (stage === 'hired') {
                      setHiring(c);
                      return;
                    }
                    transition.mutate({ id: c.id, toStage: stage });
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </div>

      {showAdd && (
        <AddCandidateDialog
          onClose={() => setShowAdd(false)}
          onCreate={(args) => create.mutateAsync(args).then(() => setShowAdd(false))}
          busy={create.isPending}
        />
      )}

      {hiring && (
        <HireDialog
          candidate={hiring}
          onClose={() => setHiring(null)}
          onHire={(args) => hire.mutateAsync({ id: hiring.id, ...args }).then((res) => {
            setHiring(null);
            // The candidate→employee handoff: jump straight to the new hire's
            // profile so PII / comp can be completed instead of dead-ending.
            if (res?.employeeId) nav(`/app/people/${res.employeeId}`);
          })}
          busy={hire.isPending}
        />
      )}

      {selectedId && (
        <CandidateDetailDrawer
          candidateId={selectedId}
          onClose={() => setSelectedId(null)}
          onHire={(c) => { setSelectedId(null); setHiring(c); }}
        />
      )}
    </AppShell>
  );
}

/**
 * Candidate detail drawer — was the biggest Hiring dead-end: a created
 * candidate could only be "advanced" and never opened, rejected, or
 * withdrawn. Uses the already-built GET /candidates/:id (candidate + event
 * timeline) and the transition endpoint's rejected/withdrawn stages.
 */
function CandidateDetailDrawer({ candidateId, onClose, onHire }: {
  candidateId: string;
  onClose: () => void;
  onHire: (c: Candidate) => void;
}) {
  const { t } = useTranslation();
  const detail = useCandidate(candidateId);
  const transition = useTransitionCandidate();
  const c = detail.data?.candidate;
  const events = detail.data?.events ?? [];

  const NEXT: Record<string, CandidateStage | null> = {
    applied: 'screen', screen: 'interview', interview: 'offer', offer: 'hired',
    hired: null, rejected: null, withdrawn: null,
  };
  const next = c ? NEXT[c.stage] : null;
  const terminal = c ? ['hired', 'rejected', 'withdrawn'].includes(c.stage) : true;

  async function reject() {
    if (!c) return;
    const reason = await promptText({
      title: t('hiring.rejectTitle', { defaultValue: 'Reject candidate' }),
      inputLabel: t('hiring.rejectReason', { defaultValue: 'Reason (optional)' }),
    });
    if (reason === null) return; // cancelled
    transition.mutate({ id: c.id, toStage: 'rejected', rejectedReason: reason || undefined });
    onClose();
  }
  async function withdraw() {
    if (!c) return;
    const ok = await confirm({
      title: t('hiring.withdrawTitle', { defaultValue: 'Mark as withdrawn?' }),
      description: t('hiring.withdrawBody', { defaultValue: 'The candidate withdrew from the process.' }),
      confirmLabel: t('hiring.withdraw', { defaultValue: 'Withdraw' }),
    });
    if (!ok) return;
    transition.mutate({ id: c.id, toStage: 'withdrawn' });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="h-full w-full max-w-md bg-white dark:bg-card shadow-glow border-s border-border p-6 overflow-y-auto">
        {!c ? (
          <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">{c.fullName}</h2>
                <p className="text-xs text-muted-foreground">{c.roleTitle} · {c.email}{c.countryCode ? ` · ${c.countryCode}` : ''}</p>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md bg-muted shrink-0">{t(`hiring.stage.${c.stage}`)}</span>
            </div>

            {c.rejectedReason && (
              <p className="mt-3 text-xs text-rose-600 bg-rose-50 dark:bg-rose-500/10 rounded-lg px-3 py-2">{c.rejectedReason}</p>
            )}
            {c.notes && <p className="mt-3 text-sm text-foreground/80 whitespace-pre-wrap">{c.notes}</p>}

            {!terminal && (
              <div className="mt-4 flex flex-wrap gap-2">
                {next === 'hired' ? (
                  <button onClick={() => onHire(c)} className="gradient-primary inline-flex h-9 items-center px-4 rounded-lg text-white text-sm font-semibold shadow-glow">{t('hiring.hire', { defaultValue: 'Hire' })}</button>
                ) : next && (
                  <button onClick={() => { transition.mutate({ id: c.id, toStage: next }); onClose(); }} className="inline-flex h-9 items-center px-4 rounded-lg bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20">{t('hiring.advance')}</button>
                )}
                <button onClick={reject} className="inline-flex h-9 items-center px-3 rounded-lg border border-rose-300 text-rose-600 text-sm font-semibold hover:bg-rose-50">{t('hiring.reject', { defaultValue: 'Reject' })}</button>
                <button onClick={withdraw} className="inline-flex h-9 items-center px-3 rounded-lg border border-border text-sm font-semibold hover:bg-muted">{t('hiring.withdraw', { defaultValue: 'Withdraw' })}</button>
              </div>
            )}

            <h3 className="mt-6 text-xs uppercase tracking-wider font-semibold text-muted-foreground">{t('hiring.timeline', { defaultValue: 'Timeline' })}</h3>
            <ul className="mt-2 space-y-2">
              {events.length === 0 && <li className="text-xs text-muted-foreground">{t('hiring.noEvents', { defaultValue: 'No activity yet.' })}</li>}
              {events.map((ev) => (
                <li key={ev.id} className="text-xs border-s-2 border-border ps-3">
                  <p className="font-medium">{ev.fromStage ? `${ev.fromStage} → ${ev.toStage ?? ''}` : (ev.toStage ?? ev.kind)}{ev.body ? ` · ${ev.body}` : ''}</p>
                  <p className="text-muted-foreground">{new Date(ev.createdAt).toLocaleString()}</p>
                </li>
              ))}
            </ul>

            <button onClick={onClose} className="mt-6 text-sm font-semibold text-muted-foreground hover:text-foreground">{t('common.close', { defaultValue: 'Close' })}</button>
          </>
        )}
      </div>
    </div>
  );
}

interface HireDialogProps {
  candidate: Candidate;
  onClose: () => void;
  onHire: (args: Omit<HireArgs, 'id'>) => Promise<unknown>;
  busy?: boolean;
}

function HireDialog({ candidate, onClose, onHire, busy }: HireDialogProps) {
  const { t } = useTranslation();
  const country = (candidate.countryCode ?? 'JP').toUpperCase();
  const isJp = country === 'JP';
  const [employmentType, setEmploymentType] = useState<HireArgs['employmentType']>(isJp ? 'seishain' : 'fte');
  const [hireDate, setHireDate] = useState(new Date().toISOString().slice(0, 10));
  const [jobTitle, setJobTitle] = useState(candidate.roleTitle);
  const [salary, setSalary] = useState(
    candidate.expectedSalaryCents ? String(Math.round(candidate.expectedSalaryCents / 100)) : '',
  );
  const [currency, setCurrency] = useState(candidate.expectedCurrency ?? (isJp ? 'JPY' : 'USD'));

  const employmentOptions = isJp
    ? (['seishain', 'keiyaku', 'gyoumu_itaku', 'part_time', 'intern'] as const)
    : (['fte', 'contractor', 'part_time', 'intern'] as const);

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center px-4">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const cents = salary ? Math.round(Number(salary) * 100) : undefined;
          void onHire({
            employmentType,
            hireDate,
            countryCode: country,
            jobTitle,
            baseSalaryCents: cents,
            salaryCurrency: currency,
            salaryPeriod: 'monthly',
          });
        }}
        className="w-full max-w-md rounded-2xl bg-white dark:bg-card shadow-glow border border-border p-6 space-y-3"
      >
        <header>
          <p className="text-xs uppercase tracking-[0.18em] text-primary font-semibold">{t('hiring.hireDialog.eyebrow')}</p>
          <h2 className="text-lg font-semibold mt-1">{t('hiring.hireDialog.title', { name: candidate.fullName })}</h2>
          <p className="text-xs text-muted-foreground">{t('hiring.hireDialog.subtitle')}</p>
        </header>

        <div>
          <label className="text-xs font-semibold text-muted-foreground">{t('hiring.hireDialog.employmentType')}</label>
          <div className="mt-1">
            <Combobox<HireArgs['employmentType']>
              value={employmentType}
              onChange={setEmploymentType}
              items={employmentOptions.map((o) => ({
                value: o,
                label: t(`employment.${o}`, { defaultValue: o }),
                badge: o,
              })) satisfies ComboboxItem<HireArgs['employmentType']>[]}
              hideSearch
              align="left"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-muted-foreground">{t('hiring.hireDialog.hireDate')}</label>
          <input
            type="date" required value={hireDate} onChange={(e) => setHireDate(e.target.value)}
            className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-muted-foreground">{t('hiring.hireDialog.jobTitle')}</label>
          <input
            type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
            className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm"
          />
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <label className="text-xs font-semibold text-muted-foreground">{t('hiring.hireDialog.salary')}</label>
            <input
              type="number" min={0} value={salary} onChange={(e) => setSalary(e.target.value)}
              className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm num"
              placeholder={isJp ? '450000' : '8000'}
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t('hiring.hireDialog.currency')}</label>
            <div className="mt-1">
              <CurrencyPicker value={currency} onChange={setCurrency} />
            </div>
          </div>
        </div>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose}
                  className="inline-flex h-10 flex-1 items-center justify-center rounded-xl border border-border text-sm font-semibold hover:bg-muted">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={busy}
                  className="gradient-primary inline-flex h-10 flex-1 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow disabled:opacity-60">
            {busy ? t('common.saving') : t('hiring.hireDialog.confirm')}
          </button>
        </div>
      </form>
    </div>
  );
}

function CandidateRow({ c, onAdvance, onSelect }: { c: Candidate; onAdvance: (s: CandidateStage) => void; onSelect: () => void }) {
  const { t } = useTranslation();
  const next: CandidateStage | null =
    c.stage === 'applied'   ? 'screen'    :
    c.stage === 'screen'    ? 'interview' :
    c.stage === 'interview' ? 'offer'     :
    c.stage === 'offer'     ? 'hired'     : null;

  return (
    <li className="px-5 py-3 flex items-center gap-3 hover:bg-muted/20 transition">
      <div className={`h-10 w-10 grid place-items-center rounded-xl bg-gradient-to-br ${STAGE_ACCENT[c.stage]} text-white text-sm font-semibold`}>
        {(c.fullName || '?').slice(0, 1).toUpperCase()}
      </div>
      <button onClick={onSelect} className="flex-1 min-w-0 text-start">
        <p className="text-sm font-semibold truncate hover:text-primary transition">{c.fullName}</p>
        <p className="text-xs text-muted-foreground truncate">
          {c.roleTitle} · {c.email}{c.countryCode ? ` · ${c.countryCode}` : ''}
        </p>
      </button>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-foreground/70 px-2 py-1 rounded-md bg-muted">
        {t(`hiring.stage.${c.stage}`)}
      </span>
      {next && (
        <button
          onClick={() => onAdvance(next)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          {t('hiring.advance')}
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

interface AddDialogProps {
  onClose: () => void;
  onCreate: (args: { fullName: string; email: string; roleTitle: string; countryCode?: string; notes?: string }) => Promise<unknown>;
  busy?: boolean;
}

function AddCandidateDialog({ onClose, onCreate, busy }: AddDialogProps) {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [countryCode, setCountryCode] = useState('JP');
  const [notes, setNotes] = useState('');

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center px-4">
      <div className="w-full max-w-md rounded-2xl bg-white dark:bg-card shadow-glow border border-border p-6">
        <h2 className="text-lg font-semibold">{t('hiring.dialog.title')}</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!fullName || !email || !roleTitle) return;
            void onCreate({ fullName, email, roleTitle, countryCode, notes: notes || undefined });
          }}
          className="mt-4 space-y-3"
        >
          <Field label={t('hiring.dialog.fullName')} value={fullName} onChange={setFullName} required />
          <Field label={t('hiring.dialog.email')} value={email} onChange={setEmail} type="email" required />
          <Field label={t('hiring.dialog.roleTitle')} value={roleTitle} onChange={setRoleTitle} required />
          <div>
            <label className="text-xs font-semibold text-muted-foreground">
              {t('hiring.dialog.country')}
              <span className="text-destructive ms-1">*</span>
            </label>
            <div className="mt-1">
              <CountryPicker value={countryCode} onChange={setCountryCode} />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{t('hiring.dialog.notes')}</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 flex-1 items-center justify-center rounded-xl border border-border text-sm font-semibold hover:bg-muted"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={busy}
              className="gradient-primary inline-flex h-10 flex-1 items-center justify-center rounded-xl text-white text-sm font-semibold shadow-glow disabled:opacity-60"
            >
              {busy ? t('common.saving') : t('hiring.dialog.add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, type = 'text', required,
}: { label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean }) {
  return (
    <div>
      <label className="text-xs font-semibold text-muted-foreground">
        {label}
        {required && <span className="text-destructive ms-1">*</span>}
      </label>
      <input
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full h-10 rounded-xl border border-border bg-card px-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
      />
    </div>
  );
}
